import { useEffect, useState } from "react";
import {
  getAgent,
  buildAgent,
  listAgentSkills,
  uploadAgentSkill,
  deleteAgentSkill,
} from "../api/agentApi";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert, Button, Snackbar, Chip,
  TextField, Accordion, AccordionSummary, AccordionDetails, Tabs, Tab, IconButton, Stack, Tooltip }
  from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GeneralTabPanel from "./AgentDetail/GeneralTabPanel.jsx";
import AgentRuns from "./AgentDetail/AgentRuns";
import DetailItem from './AgentDetail/DetailItem';
import AgentRunDrawer from "./AgentDetail/AgentRunDrawer";
import { getRequirementLabel, getToolCatalogMetadata } from "../toolCatalogMetadata";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";
import { useUser } from "./UserContext";
import { PERMISSIONS } from "../permissions";
import { AGENT_CREATED_SUCCESS_MESSAGE, getHighlightedAgentsPath } from "../agentNavigation";

const POLL_INTERVAL_BASE = 5000; // ms
const POLL_INTERVAL_MAX = 60000; // ms
const ACTIVE_AGENT_STATUSES = new Set(["pending", "creating", "building", "queued", "processing"]);
const READY_AGENT_STATUSES = new Set(["ready", "completed", "complete"]);
const COPPER = BRAND_COLORS.primary.main;
const COPPER_DARK = BRAND_COLORS.primary.dark;
const COPPER_SOFT = BRAND_COLORS.primary.soft;
const BORDER_SOFT = "#E5D4C4";
const MUTED_TEXT = "#6F6A64";
const CATALOG_GREEN_DARK = "#245F39";
const CATALOG_GREEN_SOFT = "rgba(47, 125, 74, 0.12)";
const API_SETUP_BLUE = "#1F78D1";
const API_SETUP_BLUE_SOFT = "rgba(31, 120, 209, 0.12)";

function normalizeAgentStatus(status) {
  return String(status || "").trim().toLowerCase();
}

function isActiveAgentStatus(status) {
  return ACTIVE_AGENT_STATUSES.has(normalizeAgentStatus(status));
}

function isReadyAgentStatus(status) {
  return READY_AGENT_STATUSES.has(normalizeAgentStatus(status));
}

function isSetupRequiredAgentStatus(status) {
  return normalizeAgentStatus(status) === "setup_required";
}

function formatAgentStatus(status) {
  if (isSetupRequiredAgentStatus(status)) return "API setup needed";
  return String(status || "Unverified").replace(/_/g, " ");
}

const primaryActionSx = {
  borderRadius: UI_RADIUS.button,
  px: 2.6,
  py: 1.15,
  minHeight: 48,
  fontWeight: 900,
  textTransform: "none",
  bgcolor: "primary.main",
  color: "#fff",
  boxShadow: `0 12px 24px ${BRAND_COLORS.primary.shadowHover}`,
  transition: "transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease",
  "&:hover": {
    bgcolor: "primary.dark",
    transform: "translateY(-1px)",
    boxShadow: `0 16px 30px ${BRAND_COLORS.primary.shadowStrong}`,
  },
  "&.Mui-disabled": {
    bgcolor: "#E6E0DA",
    color: "#9B9188",
    boxShadow: "none",
  },
};

const secondaryActionSx = {
  borderRadius: UI_RADIUS.button,
  px: 2.4,
  py: 1.1,
  minHeight: 48,
  fontWeight: 850,
  textTransform: "none",
  borderColor: BORDER_SOFT,
  color: COPPER_DARK,
  bgcolor: "#fff",
  transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, background-color 140ms ease",
  "&:hover": {
    borderColor: COPPER,
    bgcolor: COPPER_SOFT,
    transform: "translateY(-1px)",
    boxShadow: "0 12px 24px rgba(31, 24, 18, 0.08)",
  },
};

export default function NewAgentDetailPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { can } = useUser();
  const isDark = theme.palette.mode === "dark";
  const canRunAgent = Boolean(can?.(PERMISSIONS.AGENTS_RUN));
  const canUpdateAgent = Boolean(can?.(PERMISSIONS.AGENTS_UPDATE));
  const [agent, setAgent] = useState(null);
  const [llmSettings, setLLMSettings] = useState(undefined);
  const [error, setError] = useState("");
  const [buildStatus, setBuildStatus] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [runsRefreshToken, setRunsRefreshToken] = useState(0);
  const [value, setValue] = useState(0);
  const [skills, setSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillsError, setSkillsError] = useState("");
  const [skillName, setSkillName] = useState("");
  const [skillRootFilename, setSkillRootFilename] = useState("");
  const [skillFiles, setSkillFiles] = useState([]);
  const [skillUploading, setSkillUploading] = useState(false);
  const [skillDeletingId, setSkillDeletingId] = useState(null);
  const [creationSnackbarOpen, setCreationSnackbarOpen] = useState(Boolean(location.state?.agentCreated));
  
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  function a11yProps(index) { return { id: `tab-${index}`, 'aria-controls': `tabpanel-${index}` }; }

  useEffect(() => {
    if (location.state?.agentCreated) {
      setCreationSnackbarOpen(true);
    }
  }, [location.state?.agentCreated]);

  useEffect(() => {
    let isUnmounted = false;
    const fetchAgent = async () => {
      try {
        const data = await getAgent(agentId);
        if (!isUnmounted) {
          setAgent(data);
          setError("");
        }
      } catch {
        if (!isUnmounted) setError("Failed to load agent details.");
      }
    };
    fetchAgent();
    return () => {
      isUnmounted = true;
    };
  }, [agentId]);

  // Use agent object directly (API now returns JSON)
  const card = agent;
  const agentStatus = normalizeAgentStatus(card?.status);
  const isAgentStatusActive = isActiveAgentStatus(agentStatus);
  const isAgentSetupRequired = isReadyAgentStatus(agentStatus)
    ? false
    : isSetupRequiredAgentStatus(agentStatus) || Boolean(card?.catalog_metadata?.requires_api_setup);
  const isAgentRunnable = isReadyAgentStatus(agentStatus) && !isAgentSetupRequired;
  const isCatalogAgent = card?.agent_source === "catalog";
  const agentSourceLabel = isCatalogAgent ? "Built-in agent" : "Custom agent";
  const panelBg = isDark ? "#181411" : "#FFFEFC";
  const panelSoftBg = isDark ? "#211B16" : "#FBF8F5";
  const fieldBg = isDark ? alpha("#FFF7EF", 0.06) : "#fff";
  const borderSoft = isDark ? "rgba(224,177,132,0.24)" : BORDER_SOFT;
  const lineSoft = isDark ? "rgba(224,177,132,0.20)" : "rgba(184,115,51,0.20)";
  const mutedText = isDark ? "text.secondary" : MUTED_TEXT;
  const copperText = isDark ? "#F2C69B" : COPPER_DARK;
  const copperSoft = isDark ? "rgba(224,177,132,0.16)" : COPPER_SOFT;
  const greenText = isDark ? "#CFEFD4" : CATALOG_GREEN_DARK;
  const greenSoft = isDark ? "rgba(91,160,92,0.18)" : CATALOG_GREEN_SOFT;
  const successText = isDark ? "#CFEFD4" : "#2E7D32";
  const successSoft = isDark ? "rgba(91,160,92,0.18)" : "rgba(76, 175, 80, 0.12)";
  const setupText = isDark ? "#FFE2BD" : API_SETUP_BLUE;
  const setupSoft = isDark ? "rgba(255,184,89,0.16)" : API_SETUP_BLUE_SOFT;
  const neutralText = isDark ? "#D9C9BA" : "#4F4A45";
  const neutralSoft = isDark ? "rgba(255,247,239,0.10)" : "#F3F0ED";
  const secondaryActionThemedSx = {
    ...secondaryActionSx,
    borderColor: borderSoft,
    color: copperText,
    bgcolor: fieldBg,
    "&:hover": {
      borderColor: isDark ? "rgba(224,177,132,0.42)" : COPPER,
      bgcolor: isDark ? "rgba(224,177,132,0.12)" : COPPER_SOFT,
      transform: "translateY(-1px)",
      boxShadow: isDark ? "0 12px 24px rgba(0,0,0,0.28)" : "0 12px 24px rgba(31, 24, 18, 0.08)",
    },
  };

  useEffect(() => {
    if (window.location.hash !== "#skills" || !card?.agent_id) return undefined;

    const scrollTimer = window.setTimeout(() => {
      document.getElementById("skills")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);

    return () => window.clearTimeout(scrollTimer);
  }, [card?.agent_id, card?.version]);

  useEffect(() => {
    if (!agentId || !isAgentStatusActive) return undefined;

    let isUnmounted = false;
    let poller;

    const pollAgentStatus = async () => {
      try {
        const data = await getAgent(agentId);
        if (isUnmounted) return;

        setAgent(data);
        setError("");

        if (isActiveAgentStatus(data?.status)) {
          poller = setTimeout(pollAgentStatus, POLL_INTERVAL_BASE);
        }
      } catch {
        if (!isUnmounted) {
          setError("Failed to load agent details.");
          poller = setTimeout(pollAgentStatus, Math.min(POLL_INTERVAL_BASE * 2, POLL_INTERVAL_MAX));
        }
      }
    };

    poller = setTimeout(pollAgentStatus, POLL_INTERVAL_BASE);

    return () => {
      isUnmounted = true;
      if (poller) clearTimeout(poller);
    };
  }, [agentId, isAgentStatusActive, agentStatus]);

  useEffect(() => {
    if (!agentId || !card?.version) return undefined;

    let isUnmounted = false;
    const fetchSkills = async () => {
      setSkillsLoading(true);
      setSkillsError("");
      try {
        const data = await listAgentSkills(agentId, card.version);
        if (!isUnmounted) {
          setSkills(Array.isArray(data?.skills) ? data.skills : []);
        }
      } catch (err) {
        if (!isUnmounted) {
          console.error(err);
          setSkillsError("Failed to load attached skills.");
        }
      } finally {
        if (!isUnmounted) setSkillsLoading(false);
      }
    };
    fetchSkills();
    return () => {
      isUnmounted = true;
    };
  }, [agentId, card?.version]);

    useEffect(() => {
      if (card) {
        const parsedObject = JSON.parse(card?.llm_settings || '{}');
        setLLMSettings(parsedObject);
      }
    }, [card]);

   const handleBuild = async () => {
      if (!canUpdateAgent) return;
      setBuildStatus(null);
      setIsBuilding(true);
      // Immediately set agent status to 'building' for UI feedback and polling
      setAgent(prev => prev ? { ...prev, status: "building" } : prev);
      setError(""); // Clear any previous error
      try {
        const result = await buildAgent(agentId);
        setBuildStatus(result);
        setSnackbarOpen(true);
        const updated = await getAgent(agentId);
        setAgent(updated);
      } catch (e) {
        setBuildStatus({ status: "error", message: "Failed to build agent." });
        setSnackbarOpen(true);
      } finally {
        setIsBuilding(false);
      }
    };

  const refreshSkills = async () => {
    if (!agentId || !card?.version) return;
    setSkillsLoading(true);
    setSkillsError("");
    try {
      const data = await listAgentSkills(agentId, card.version);
      setSkills(Array.isArray(data?.skills) ? data.skills : []);
    } catch (err) {
      console.error(err);
      setSkillsError("Failed to load attached skills.");
    } finally {
      setSkillsLoading(false);
    }
  };

  const handleSkillUpload = async () => {
    if (!canUpdateAgent) return;
    if (!card?.version || !skillFiles.length) return;
    setSkillUploading(true);
    setSkillsError("");
    try {
      const form = new FormData();
      form.append("version", String(card.version));
      if (skillName.trim()) form.append("name", skillName.trim());
      if (skillRootFilename.trim()) form.append("root_filename", skillRootFilename.trim());
      skillFiles.forEach(file => form.append("files", file));
      await uploadAgentSkill(agentId, form);
      setSkillName("");
      setSkillRootFilename("");
      setSkillFiles([]);
      await refreshSkills();
      const updated = await getAgent(agentId);
      setAgent(updated);
    } catch (err) {
      console.error(err);
      setSkillsError("Failed to attach skill bundle. Confirm the files are Markdown or text and the root filename matches an uploaded file.");
    } finally {
      setSkillUploading(false);
    }
  };

  const handleDeleteSkill = async (skillId) => {
    if (!canUpdateAgent) return;
    setSkillDeletingId(skillId);
    setSkillsError("");
    try {
      await deleteAgentSkill(agentId, skillId);
      await refreshSkills();
      const updated = await getAgent(agentId);
      setAgent(updated);
    } catch (err) {
      console.error(err);
      setSkillsError("Failed to delete skill.");
    } finally {
      setSkillDeletingId(null);
    }
  };
  

  const handleRunAgent = () => {
    if (!canRunAgent) return;
    navigate(`/agent/${card?.agent_id || agentId}/chatAgent`);
  }

  if (error) {
    return (
      <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!agent) {
    return (
      <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }




  const handleEdit = () => {
    // Always pass the latest card as state for pre-population
    navigate("/agent/create", { state: { agentCard: card } });
  };

  const handleViewCreatedAgentInDashboard = () => {
    const createdAgentId = location.state?.agentId || card?.agent_id || agentId;
    setCreationSnackbarOpen(false);
    navigate(getHighlightedAgentsPath(createdAgentId));
  };

  const toolSetupItems = Array.isArray(card?.tools)
    ? card.tools.map((tool) => ({
        tool,
        metadata: getToolCatalogMetadata(tool || {}),
      }))
    : [];
  const allToolSetupRequirements = toolSetupItems.flatMap((item) => item.metadata.setupRequirements || []);
  const missingToolSetupRequirements = allToolSetupRequirements.filter(
    (requirement) => requirement.required !== false && !requirement.satisfied
  );
  
  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2.5, flexWrap: { xs: "wrap", md: "nowrap" } }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h3" component="h1" sx={{ ...PAGE_TYPOGRAPHY.heading, overflowWrap: "anywhere" }}>
              {agent.display_name || card.agent_name || card.name || "Agent Detail"}
            </Typography>
            {isAgentStatusActive && (
              <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
                Preparing the agent. This page updates automatically.
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            useFlexGap
            flexWrap="wrap"
            sx={{ flexShrink: 0, maxWidth: { xs: "100%", md: "44%" }, pt: { xs: 0, md: 0.5 } }}
          >
            <Chip
              label={agentSourceLabel}
              sx={{
                bgcolor: isCatalogAgent ? greenSoft : copperSoft,
                color: isCatalogAgent ? greenText : copperText,
                border: `1px solid ${isCatalogAgent ? (isDark ? "rgba(145,205,153,0.30)" : "rgba(47,125,74,0.32)") : (isDark ? "rgba(224,177,132,0.30)" : "rgba(184,115,51,0.36)")}`,
                fontWeight: 850,
              }}
            />
            <Chip
              icon={<AutoAwesomeIcon />}
              label={skills.length ? `${skills.length} skill${skills.length === 1 ? "" : "s"} attached` : "No skills attached"}
              sx={{
                bgcolor: skills.length ? copperSoft : neutralSoft,
                color: skills.length ? copperText : neutralText,
                border: `1px solid ${skills.length ? (isDark ? "rgba(224,177,132,0.30)" : "rgba(184,115,51,0.36)") : (isDark ? "rgba(255,247,239,0.16)" : "#E3DDD6")}`,
                fontWeight: 850,
                "& .MuiChip-icon": {
                  color: skills.length ? copperText : neutralText,
                },
              }}
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
          {canRunAgent && (
            <Button
              variant="contained"
              onClick={handleRunAgent}
              disabled={!isAgentRunnable}
              startIcon={isAgentStatusActive ? <CircularProgress size={17} sx={{ color: "inherit" }} /> : <PlayArrowIcon />}
              sx={{ ...primaryActionSx, width: { xs: "100%", sm: "auto" } }}
            >
              {isAgentStatusActive ? "Preparing" : "Run Agent"}
            </Button>
          )}
          {canUpdateAgent && (
            <Button
              variant="outlined"
              onClick={handleEdit}
              startIcon={<EditOutlinedIcon />}
              sx={{ ...secondaryActionThemedSx, width: { xs: "100%", sm: "auto" } }}
            >
              Edit
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleBuild}
            sx={{ borderRadius: UI_RADIUS.button, textTransform: 'none', color: "primary", display: 'none' }}
          >
            Build Agent
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="dashboard tabs">
          <Tab sx={{ textTransform: 'none' }} label="Agent Details" {...a11yProps(0)} />
          <Tab sx={{ textTransform: 'none' }} label="Agent Run History" {...a11yProps(1)} />

          {/* <Tab sx={{ textTransform: 'none' }} label="Instance Details" {...a11yProps(3)} /> */}
        </Tabs>
      </Box>
      {/* List style agent info */}
      <GeneralTabPanel value={value} index={0} sx={{ p: 0 }}>
      <Box sx={{ mb: 0, p: 2 }}>
        
        {/* <AgentPromptAndResponse /> */}
        <DetailItem label="Role" value={card.role} />
        <DetailItem label="Agent ID" value={card.agent_id} />
        <DetailItem label="Display Name" value={card.display_name} />
        <DetailItem label="Description" 
                    value={card.description} />
        <DetailItem label="Owner" value={card.owner || "Unknown"} />
        <DetailItem label="Status" value={
              <Chip
                    label={isAgentSetupRequired ? "API setup needed" : formatAgentStatus(card.status)}
                    sx={{
                      bgcolor: isAgentRunnable ? successSoft : isAgentSetupRequired ? setupSoft : isAgentStatusActive ? copperSoft : neutralSoft,
                      color: isAgentRunnable ? successText : isAgentSetupRequired ? setupText : isAgentStatusActive ? copperText : neutralText,
                      border: `1px solid ${isAgentRunnable ? (isDark ? "rgba(145,205,153,0.30)" : "rgba(76, 175, 80, 0.32)") : isAgentSetupRequired ? (isDark ? "rgba(255,184,89,0.30)" : "rgba(31,120,209,0.34)") : isAgentStatusActive ? borderSoft : (isDark ? "rgba(255,247,239,0.16)" : "#DDD6CF")}`,
                      fontWeight: 800,
                      borderRadius: "9999px", // fully rounded
                      height: 28,
                      px: 1.5,
                    }}
                />
            }
        />
        <DetailItem label="Number of Executions" value={card.number_of_executions ?? 0} />
        <DetailItem label="Tool Setup" value={
          allToolSetupRequirements.length > 0 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={missingToolSetupRequirements.length === 0 ? "Ready" : `${missingToolSetupRequirements.length} setup item${missingToolSetupRequirements.length === 1 ? "" : "s"}`}
                sx={{
                  bgcolor: missingToolSetupRequirements.length === 0 ? successSoft : copperSoft,
                  color: missingToolSetupRequirements.length === 0 ? successText : copperText,
                  border: `1px solid ${missingToolSetupRequirements.length === 0 ? (isDark ? "rgba(145,205,153,0.30)" : "rgba(76, 175, 80, 0.32)") : (isDark ? "rgba(224,177,132,0.30)" : "rgba(184,115,51,0.30)")}`,
                  fontWeight: 850,
                }}
              />
              {allToolSetupRequirements.slice(0, 4).map((requirement) => (
                <Chip
                  key={requirement.id}
                  label={getRequirementLabel(requirement)}
                  size="small"
                  sx={{
                    bgcolor: requirement.satisfied ? successSoft : setupSoft,
                    color: requirement.satisfied ? successText : setupText,
                    border: `1px solid ${requirement.satisfied ? (isDark ? "rgba(145,205,153,0.30)" : "rgba(76, 175, 80, 0.28)") : (isDark ? "rgba(255,184,89,0.30)" : "#EFD0AF")}`,
                    fontWeight: 800,
                  }}
                />
              ))}
              {allToolSetupRequirements.length > 4 && (
                <Chip label={`+${allToolSetupRequirements.length - 4} more`} size="small" sx={{ bgcolor: neutralSoft, color: neutralText, fontWeight: 800 }} />
              )}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: mutedText }}>No external tool setup required.</Typography>
          )
        } />
        <DetailItem label="Tools" value={
          <>
          {Array.isArray(card.tools) && card.tools.length > 0 ? (
            
            <Box sx={{}}>
              {toolSetupItems.map(({ tool, metadata }, idx) => (
                <Box key={idx} sx={{ mb: 1.4 }}>
                  <Typography variant="body1" sx={{ fontWeight: 650 }}>{tool.name || tool.tool_name || `Tool ${idx + 1}`}</Typography>
                  {tool.description && (
                    <Typography variant="caption" sx={{ color: mutedText, display: "block" }}>{tool.description}</Typography>
                  )}
                  <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 0.7 }}>
                    <Chip label={metadata.collection} size="small" variant="outlined" sx={{ height: 22, borderColor: borderSoft, color: mutedText }} />
                    <Chip label={metadata.canonicalCategory.label} size="small" variant="outlined" sx={{ height: 22, borderColor: borderSoft, color: mutedText }} />
                    {(metadata.setupRequirements || []).slice(0, 2).map((requirement) => (
                      <Chip
                        key={requirement.id}
                        label={getRequirementLabel(requirement)}
                        size="small"
                        sx={{
                          height: 22,
                          bgcolor: requirement.satisfied ? successSoft : setupSoft,
                          color: requirement.satisfied ? successText : setupText,
                          border: `1px solid ${requirement.satisfied ? (isDark ? "rgba(145,205,153,0.30)" : "rgba(76, 175, 80, 0.28)") : (isDark ? "rgba(255,184,89,0.30)" : "#EFD0AF")}`,
                          fontWeight: 750,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Box>)
            : (
            <Typography variant="body2" sx={{ mb: 2, color: mutedText }}>No tools found for this agent.</Typography>
            )}
          </>}
        />
        
        <DetailItem label="LLM" value={card.llm} />
        <DetailItem label="LLM Temperature" value={llmSettings?.temperature} />
        <DetailItem label="LLM Maximum tokens" value={llmSettings?.max_tokens} />
        <DetailItem label="Created" value={card.date || ""} />    
        
         <DetailItem label="Associated Collections" value={
          <>
            {Array.isArray(card.rag) && card.rag.length > 0 ? (
            <Box sx={{ mb: 2, pl: 2 }}>
              {card.rag.map((index, idx) => (
                <Box key={index.id || idx} sx={{ mb: 1, p: 1, bgcolor: successSoft, color: "text.primary", border: `1px solid ${isDark ? "rgba(145,205,153,0.24)" : "rgba(76, 175, 80, 0.18)"}`, borderRadius: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {index.name || `Index ${idx + 1}`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: mutedText }}>
                    ID: {index.id}
                  </Typography>
                  {index.rag_type && (
                    <Typography variant="caption" sx={{ color: mutedText, ml: 1 }}>
                      Type: {index.rag_type}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
            ) : (
              <Typography variant="body1" sx={{ mb: 2, color: mutedText }}>No collections associated with this agent.</Typography>
            )}
          </>
         }
        />

        <Box id="skills" sx={{
          mt: 2.5,
          p: 2.2,
          border: `1px solid ${borderSoft}`,
          borderRadius: "14px",
          bgcolor: panelBg,
          color: "text.primary",
          scrollMarginTop: "96px",
        }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: 0, color: "text.primary" }}>
                Attached Skills
              </Typography>
              <Typography variant="body2" sx={{ color: mutedText, mt: 0.3 }}>
                Add Markdown instruction packs such as AGENTS.md, CLAUDE.md, or SKILL.md. Referenced local Markdown files are fused into the runtime prompt.
              </Typography>
            </Box>
            <Chip
              label={`${skills.length} attached`}
              sx={{
                bgcolor: skills.length ? copperSoft : neutralSoft,
                color: skills.length ? copperText : neutralText,
                border: `1px solid ${skills.length ? (isDark ? "rgba(224,177,132,0.30)" : "rgba(184,115,51,0.28)") : (isDark ? "rgba(255,247,239,0.16)" : "#E3DDD6")}`,
                fontWeight: 850,
              }}
            />
          </Stack>

          {skillsError ? <Alert severity="error" sx={{ mt: 1.5 }}>{skillsError}</Alert> : null}

          {canUpdateAgent && (
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} sx={{ mt: 1.8 }} alignItems={{ xs: "stretch", md: "center" }}>
              <TextField
                label="Skill name"
                value={skillName}
                onChange={e => setSkillName(e.target.value)}
                size="small"
                placeholder="Optional"
                sx={{ minWidth: { md: 220 } }}
              />
              <TextField
                label="Root file"
                value={skillRootFilename}
                onChange={e => setSkillRootFilename(e.target.value)}
                size="small"
                placeholder="AGENTS.md, SKILL.md, etc."
                sx={{ minWidth: { md: 240 } }}
              />
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={secondaryActionThemedSx}
              >
                Choose files
                <input
                  hidden
                  multiple
                  type="file"
                  accept=".md,.markdown,.txt"
                  onChange={e => setSkillFiles(Array.from(e.target.files || []))}
                />
              </Button>
              <Button
                variant="contained"
                onClick={handleSkillUpload}
                disabled={skillUploading || !skillFiles.length || !isAgentRunnable}
                startIcon={skillUploading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <UploadFileIcon />}
                sx={{ ...primaryActionSx, minHeight: 42 }}
              >
                Attach
              </Button>
            </Stack>
          )}

          {skillFiles.length ? (
            <Typography variant="caption" sx={{ display: "block", color: mutedText, mt: 1 }}>
              Selected: {skillFiles.map(file => file.name).join(", ")}
            </Typography>
          ) : null}

          <Box sx={{ mt: 1.6 }}>
            {skillsLoading ? (
              <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} sx={{ color: COPPER }} />
                <Typography variant="body2" sx={{ color: mutedText }}>Loading skills...</Typography>
              </Box>
            ) : skills.length ? (
              <Stack spacing={1}>
                {skills.map(skill => (
                  <Box
                    key={skill.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      p: 1.4,
                      borderRadius: "12px",
                      border: `1px solid ${lineSoft}`,
                      bgcolor: panelSoftBg,
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "10px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: copperSoft,
                        color: copperText,
                        flexShrink: 0,
                      }}>
                        <DescriptionOutlinedIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 850, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {skill.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: mutedText }}>
                          Root: {skill.root_filename || skill.source_filename} · {skill.referenced_files?.length || 0} file{(skill.referenced_files?.length || 0) === 1 ? "" : "s"}
                        </Typography>
                      </Box>
                    </Stack>
                    {canUpdateAgent && (
                      <Tooltip title="Remove skill">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSkill(skill.id)}
                            disabled={skillDeletingId === skill.id || !isAgentRunnable}
                            sx={{ color: copperText }}
                          >
                            {skillDeletingId === skill.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: mutedText, py: 1.4 }}>
                No skills attached yet.
              </Typography>
            )}
          </Box>
        </Box>
        
        {/* <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Artifacts</Typography>
        <Box sx={{ display: "flex"}}>
          <Link 
            component={RouterLink}
            to={`/agent/${agent.agent_id}/artifacts`} 
            tyle={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}>
              {"View Details"}
          </Link>
      </Box> */}
      
      </Box>
      {/* Build status messages */}
      {/* Build status messages related to 'building' are now removed */}
      {canRunAgent && isAgentRunnable && (
        <Alert severity="success" sx={{ mt: 0}}>Agent is now Ready for Execution!</Alert>
      )}
      {card.status === "failed" && (
        <Alert severity="error" sx={{ mt: 0 }}>Agent build failed: {card.message}</Alert>
      )}
      {buildStatus && buildStatus.status === "error" && card.status !== "building" && (
        <Accordion sx={{ mt: 2, background: '#ffebee', borderRadius: 1, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Build Error
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
              {buildStatus.output || buildStatus.message}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      {buildStatus && buildStatus.status === "success" && (
        <Accordion sx={{ mt: 2, background: '#e3f2fd', borderRadius: 1, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Build Output
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
              {buildStatus.output || buildStatus.message}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={
          (buildStatus && buildStatus.status === "started" && "Agent build started.") ||
          (buildStatus && buildStatus.status === "success" && "Agent built successfully.") ||
          (buildStatus && buildStatus.status === "error" && card.status !== "building" && buildStatus.message)
        }
      />
      <Snackbar
        open={creationSnackbarOpen}
        autoHideDuration={8000}
        onClose={() => setCreationSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setCreationSnackbarOpen(false)}
          action={
            <Button color="inherit" size="small" onClick={handleViewCreatedAgentInDashboard}>
              View in Dashboard
            </Button>
          }
        >
          {AGENT_CREATED_SUCCESS_MESSAGE}
        </Alert>
      </Snackbar>
      </GeneralTabPanel>
    <GeneralTabPanel value={value} index={1} sx={{ p: 0 }}>
      <AgentRuns agentId={card.agent_id} agentVersion={card.version} refreshToken={runsRefreshToken} />
    </GeneralTabPanel>
      
      {canRunAgent && (
        <AgentRunDrawer
          agentCard={agent}
          agentID={card.agent_id}
          agentName={card.name}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          onRunComplete={() => setRunsRefreshToken((prev) => prev + 1)}
        />
      )}
    </Box>
  );
}
